import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import {
  HeartPulse,
  CheckCircle2,
  Heart,
  Loader2,
  AlertCircle,
  Shield,
  Plane,
  Building2,
  Landmark,
  HelpCircle,
  Skull,
} from "lucide-react";

/**
 * Public page a trusted contact lands on after clicking the "Responder ahora"
 * button in the escalation email. Feb 2026 revamp — replaces the previous
 * two-button (alive / deceased) UI with a 6-option chooser so the contact can
 * express nuance (vacation, hospital, prison, don't know, deceased).
 *
 * Each option maps to a `{vote, reason, suspend_days}` triple accepted by
 * POST /api/pol/confirm/{token}. See POL_REASON_SUSPEND_DAYS on the backend.
 */
const OPTIONS = [
  {
    id: "fine",
    icon: Heart,
    label: (name) => `${name} está bien`,
    hint: "Extiende la Fe de Vida automáticamente",
    payload: { vote: "alive", reason: "fine" },
    tone: "gold",
  },
  {
    id: "vacation",
    icon: Plane,
    label: () => "Está de vacaciones",
    hint: "Pausa 30 días",
    payload: { vote: "alive", reason: "vacation" },
    tone: "soft",
  },
  {
    id: "hospital",
    icon: Building2,
    label: () => "Está en el hospital",
    hint: "Pausa 60 días",
    payload: { vote: "alive", reason: "hospital" },
    tone: "soft",
  },
  {
    id: "prison",
    icon: Landmark,
    label: () => "Está en prisión",
    hint: "Pausa 180 días",
    payload: { vote: "alive", reason: "prison" },
    tone: "soft",
  },
  {
    id: "dont_know",
    icon: HelpCircle,
    label: () => "No lo sé",
    hint: "No cuenta como voto — seguirás recibiendo avisos",
    payload: null, // handled locally: dismiss without voting
    tone: "muted",
  },
  {
    id: "deceased",
    icon: Skull,
    label: (name) => `${name} ha fallecido`,
    hint: "KeepSoul liberará el legado digital a los herederos designados",
    payload: { vote: "deceased", reason: "deceased" },
    tone: "danger",
  },
];

export default function PolConfirm() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sending, setSending] = useState(false);
  const [voted, setVoted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/pol/confirm/${token}`);
        setInfo(r.data);
        if (r.data.already_voted) setVoted(true);
      } catch {
        setError("Este enlace no es válido o ha expirado.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!selected) return;
    const opt = OPTIONS.find((o) => o.id === selected);
    if (!opt) return;
    if (opt.payload === null) {
      // "No lo sé" → local dismiss, don't hit the API
      setDismissed(true);
      return;
    }
    setSending(true);
    setSubmitError(null);
    try {
      await api.post(`/pol/confirm/${token}`, opt.payload);
      setVoted(true);
    } catch (e) {
      setSubmitError(
        e?.response?.data?.detail?.message ||
          e?.response?.data?.detail ||
          "No se pudo registrar tu respuesta. Reintenta en unos minutos."
      );
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-gold animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-4">
        <div className="glass-card rounded-md p-12 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-6" strokeWidth={1.2} />
          <h1 className="font-display text-3xl mb-3 text-gold-light">Enlace no válido</h1>
          <p className="text-muted">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-white flex flex-col">
      <header className="glass-header">
        <div className="max-w-3xl mx-auto px-6 py-5">
          <Logo size={28} />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="glass-card rounded-md p-8 md:p-12 max-w-2xl w-full gold-glow">
          {voted ? (
            <div className="text-center fade-in" data-testid="pol-confirm-done">
              <CheckCircle2 className="w-14 h-14 text-gold mx-auto mb-6" strokeWidth={1.2} />
              <h1 className="font-display text-4xl mb-4">Gracias, {info.contact_name}.</h1>
              <p className="text-muted">
                Tu respuesta ha sido registrada. KeepSoul actuará conforme a lo
                que {info.user_name} dejó escrito.
              </p>
            </div>
          ) : dismissed ? (
            <div className="text-center fade-in" data-testid="pol-confirm-dismissed">
              <HelpCircle className="w-14 h-14 text-gold mx-auto mb-6" strokeWidth={1.2} />
              <h1 className="font-display text-3xl mb-4">Sin problema, {info.contact_name}.</h1>
              <p className="text-muted">
                No hemos registrado ningún voto. Te enviaremos otro aviso en
                unos días. Si averiguas algo antes, puedes volver a abrir este
                mismo enlace y responder.
              </p>
            </div>
          ) : (
            <div className="space-y-6 fade-in">
              <div className="text-center">
                <HeartPulse className="w-12 h-12 text-gold mx-auto mb-4" strokeWidth={1.2} />
                <div className="font-label text-gold mb-3">Confirmación de Fe de Vida</div>
                <h1 className="font-display text-3xl md:text-4xl mb-2">
                  Hola {info.contact_name}
                </h1>
                <p className="text-muted">
                  <span className="text-gold-light">{info.user_name}</span> te eligió como
                  contacto de confianza y no ha confirmado su Fe de Vida durante el
                  período de gracia.
                </p>
              </div>

              <div className="divider-gold" />

              <div>
                <p className="text-gold-light mb-4 font-medium">
                  ¿Cuál es la situación de {info.user_name}?
                </p>
                <div className="grid gap-2.5">
                  {OPTIONS.map((o) => {
                    const Icon = o.icon;
                    const active = selected === o.id;
                    const border =
                      o.tone === "danger"
                        ? active ? "border-red-500 bg-red-500/10" : "border-red-500/30 hover:border-red-500/60"
                        : o.tone === "muted"
                        ? active ? "border-gold-subtle bg-obsidian-2" : "border-gold-subtle/40 hover:border-gold-subtle"
                        : o.tone === "gold"
                        ? active ? "border-gold bg-gold/10" : "border-gold-subtle hover:border-gold"
                        : active ? "border-gold bg-obsidian-2" : "border-gold-subtle hover:border-gold-subtle";
                    const iconColor =
                      o.tone === "danger" ? "text-red-400" :
                      o.tone === "muted" ? "text-faded" : "text-gold";
                    return (
                      <button
                        key={o.id}
                        onClick={() => setSelected(o.id)}
                        className={`text-left rounded-md border ${border} px-4 py-3 flex items-start gap-3 transition`}
                        data-testid={`pol-option-${o.id}`}
                      >
                        <Icon className={`w-5 h-5 ${iconColor} mt-0.5 shrink-0`} strokeWidth={1.5} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{o.label(info.user_name)}</div>
                          <div className="text-xs text-muted mt-0.5">{o.hint}</div>
                        </div>
                        {active && (
                          <CheckCircle2 className="w-5 h-5 text-gold shrink-0" strokeWidth={1.5} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {submitError && (
                <div
                  className="rounded-md border border-red-500/40 bg-red-500/5 text-red-300 text-xs px-4 py-3"
                  role="alert"
                  data-testid="pol-submit-error"
                >
                  {submitError}
                </div>
              )}

              <button
                onClick={() => {
                  if (selected === "deceased") {
                    const ok = window.confirm(
                      `¿Confirmas el fallecimiento de ${info.user_name}? KeepSoul iniciará el protocolo de entrega del legado digital a los herederos designados. Esta acción se registra con fecha, hora y motivo.`
                    );
                    if (!ok) return;
                  }
                  submit();
                }}
                disabled={!selected || sending}
                className={`w-full justify-center py-4 rounded-md font-medium transition ${
                  selected === "deceased"
                    ? "bg-red-900 hover:bg-red-800 text-white"
                    : "btn-gold"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
                data-testid="pol-submit"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Enviar respuesta"}
              </button>

              {/* Cookie banner is ~186px on desktop; give the CTA
                  breathing room so the primary action is not visually
                  behind the banner on first paint. */}
              <div className="h-40" aria-hidden="true" />

              <p className="text-xs text-faded text-center flex items-center justify-center gap-2 pt-4 border-t border-gold-subtle">
                <Shield className="w-3 h-3" />
                Tu voto es privado. Solo {info.user_name} eligió quién recibe cada cosa.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
